import { useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Phone, ShieldCheck, UserCheck, Scale, FileCheck, CalendarClock, Calculator,
  Home, Check, Loader2, MapPin, ArrowDown, XCircle, CheckCircle2, TrendingUp,
} from "lucide-react";
import { LANDINGS, ZONES_SERVED } from "@/data/landings";
import NotFound from "@/pages/NotFound";
import { trackMetaLead } from "@/components/MetaPixel";
import { CanoneCalculator } from "@/components/CanoneCalculator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { ShieldCheck, UserCheck, Scale, FileCheck, CalendarClock, Calculator, Home, TrendingUp };

const SITE = "https://www.semplificaffitto.it";

// JSON-LD (Service, FAQPage, BreadcrumbList) iniettato lato client: i bot ricevono
// l'HTML renderizzato dalla piattaforma, quindi il markup deve esistere anche nella SPA.
const injectLandingJsonLd = (slug, cfg) => {
  const url = `${SITE}/${slug}`;
  const scripts = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: cfg.h1,
      description: cfg.description,
      provider: {
        "@type": "Organization",
        name: "Semplificaffitto.it — Edilstudio S.r.l.",
        url: `${SITE}/`,
        telephone: "+39 055 031 7244",
        address: { "@type": "PostalAddress", addressLocality: "Scandicci", addressRegion: "FI", addressCountry: "IT" },
      },
      areaServed: ["Firenze", "Scandicci", "Lastra a Signa", "Signa", "Campi Bisenzio", "Sesto Fiorentino"],
      url,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (cfg.faqs || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: cfg.h1, item: url },
      ],
    },
  ];
  return scripts.map((data) => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.landingLd = "1";
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
    return el;
  });
};

const inputCls =
  "w-full rounded-xl bg-white border border-ink/15 px-4 py-3.5 text-base text-ink placeholder:text-ink/35 focus:outline-none focus:border-azure focus:ring-1 focus:ring-azure";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function LandingPage({ slug: slugProp }) {
  const { lpSlug } = useParams();
  const { pathname } = useLocation();
  const slug = lpSlug || slugProp || pathname.replace(/^\/+|\/+$/g, "");
  const cfg = LANDINGS[slug];

  const [stats, setStats] = useState(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nome: "", telefono: "", email: "", citta: "", tipologia: "Appartamento", indirizzo: "", note: "", privacy: false });

  useEffect(() => {
    if (!cfg) return;
    document.title = cfg.title;
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", cfg.description);
    const ldScripts = injectLandingJsonLd(slug, cfg);
    setForm((f) => ({ ...f, citta: cfg.cittaDefault }));
    axios.get(`${API}/stats`).then(({ data }) => setStats(data)).catch(() => {});
    return () => ldScripts.forEach((el) => el.remove());
  }, [cfg]);

  if (!cfg) return <NotFound />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Devi accettare l'informativa privacy per inviare la richiesta.");
      return;
    }
    setLoading(true);
    try {
      const noteParts = [form.indirizzo && `Immobile: ${form.indirizzo}`, form.note].filter(Boolean);
      const { data } = await axios.post(`${API}/leads`, { ...form, indirizzo: undefined, note: noteParts.join(" — "), fonte: cfg.fonte });
      toast.success(data.message || "Richiesta inviata!");
      trackMetaLead();
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Errore nell'invio. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    const targetId = slug === "canone-concordato-firenze" ? "canone-calculator" : "richiedi";
    document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-paper" data-testid={`landing-page-${cfg.slug}`}>
      {/* Header minimo */}
      <header className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-5 sm:px-8 py-5 max-w-6xl mx-auto">
        <Link to="/" data-testid="landing-logo-link">
          <img src="/assets/logo-nuovo.png" alt="Semplificaffitto.it" className="h-7 w-auto brightness-0 invert" />
        </Link>
        <a
          href="tel:+390550317244"
          data-testid="landing-phone-cta"
          className="inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-ink font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
        >
          <Phone size={15} /> 055 031 7244
        </a>
      </header>

      {/* Hero */}
      <section className="relative bg-ink text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-32 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-azure mb-5">{cfg.eyebrow}</p>
            <h1 className="font-serif font-bold text-4xl sm:text-5xl leading-[1.08] mb-6">{cfg.h1}</h1>
            <p className="text-white/65 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">{cfg.sub}</p>
            <div className={`flex flex-wrap items-center gap-4 ${slug === "canone-concordato-firenze" ? "mb-3" : "mb-8"}`}>
              <button
                onClick={scrollToForm}
                data-testid="landing-cta-form-btn"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-ink font-semibold rounded-full px-7 py-4 text-sm transition-colors"
              >
                {cfg.ctaText || "Richiedi la valutazione gratuita"} <ArrowDown size={16} />
              </button>
            </div>
            {slug === "canone-concordato-firenze" && (
              <p className="text-xs text-white/45 mb-5 max-w-md leading-relaxed" data-testid="landing-cta-note">
                Il simulatore è gratuito. Il calcolo esatto secondo l'accordo territoriale è gratuito per chi aderisce al protocollo Semplificaffitto.it.
              </p>
            )}
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {cfg.bullets.map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-white/70">
                  <Check size={15} className="text-azure" /> {b}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <img src="/assets/hero-casa.jpg" alt="Immobile in affitto gestito da Semplificaffitto" className="rounded-3xl w-full object-cover aspect-[4/3]" />
            <img
              src="/assets/takes-care-logo.jpg"
              alt="Semplificaffitto Takes Care"
              className="absolute -bottom-8 -right-6 w-32 h-32 rounded-full shadow-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="border-b border-ink/10 bg-white" data-testid="landing-stats">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-3 gap-4 text-center">
            {[
              [stats.immobili, "immobili gestiti"],
              [stats.contratti, "contratti registrati"],
              [stats.proprietari, "proprietari felici"],
            ].map(([n, label]) => (
              <div key={label}>
                <p className="font-serif font-bold text-3xl sm:text-4xl text-ink">{n}+</p>
                <p className="text-xs sm:text-sm text-ink/50 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Calcolatore canone concordato (solo landing dedicata) */}
      {slug === "canone-concordato-firenze" && <CanoneCalculator />}

      {/* Benefici */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp()} className="font-serif font-bold text-3xl sm:text-4xl text-ink mb-12 max-w-2xl">
          Perché i proprietari della tua zona ci scelgono
        </motion.h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {cfg.benefits.map((b, i) => {
            const Icon = ICONS[b.icon] || ShieldCheck;
            return (
              <motion.div key={b.t} {...fadeUp(i * 0.08)} className="bg-white rounded-2xl border border-ink/10 p-7">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-azure/10 text-brandblue mb-4">
                  <Icon size={20} />
                </span>
                <h3 className="font-serif font-bold text-lg text-ink mb-2">{b.t}</h3>
                <p className="text-sm text-ink/60 leading-relaxed">{b.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Tabella di contrasto */}
      {cfg.comparison && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20" data-testid="landing-comparison">
          <motion.h2 {...fadeUp()} className="font-serif font-bold text-3xl sm:text-4xl text-ink mb-10 max-w-2xl">
            Quello che cambia con Semplificaffitto
          </motion.h2>
          <motion.div {...fadeUp(0.05)} className="bg-white rounded-2xl border border-ink/10 overflow-hidden">
            <div className="grid grid-cols-2 text-xs sm:text-sm font-semibold border-b border-ink/10">
              <div className="px-5 sm:px-7 py-4 text-ink/45">Da solo o con un'agenzia tradizionale</div>
              <div className="px-5 sm:px-7 py-4 text-brandblue bg-azure/5">Con Semplificaffitto.it</div>
            </div>
            {cfg.comparison.map((row) => (
              <div key={row.plain} className="grid grid-cols-2 border-b border-ink/5 last:border-0">
                <div className="px-5 sm:px-7 py-4 text-sm text-ink/55 flex items-start gap-2.5">
                  <XCircle size={16} className="text-ink/25 shrink-0 mt-0.5" /> {row.plain}
                </div>
                <div className="px-5 sm:px-7 py-4 text-sm text-ink font-medium bg-azure/5 flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-azure shrink-0 mt-0.5" /> {row.ours}
                </div>
              </div>
            ))}
          </motion.div>
        </section>
      )}

      {/* Come funziona */}
      <section className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <motion.h2 {...fadeUp()} className="font-serif font-bold text-3xl sm:text-4xl mb-12">
            Come funziona, in tre passi
          </motion.h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {cfg.steps.map((s, i) => (
              <motion.div key={s.t} {...fadeUp(i * 0.1)} className="rounded-2xl border border-white/12 bg-white/[0.04] p-7">
                <span className="font-serif font-bold text-4xl text-azure">{i + 1}</span>
                <h3 className="font-serif font-bold text-lg mt-3 mb-2">{s.t}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Zone servite */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp()} className="font-serif font-bold text-3xl sm:text-4xl text-ink mb-4">
          La tua zona è coperta
        </motion.h2>
        <motion.p {...fadeUp(0.05)} className="text-ink/60 text-base max-w-2xl mb-8 leading-relaxed">
          La nostra sede è a Scandicci e lavoriamo ogni giorno su Firenze e tutta l'area metropolitana:
          conosciamo i prezzi reali di ogni quartiere e di ogni comune, e sappiamo chi sta cercando casa oggi.
        </motion.p>
        <div className="flex flex-wrap gap-2.5">
          {ZONES_SERVED.map((z) => (
            <span key={z} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-ink/10 px-4 py-2 text-sm text-ink/70">
              <MapPin size={13} className="text-azure" /> {z}
            </span>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-ink/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
          <motion.h2 {...fadeUp()} className="font-serif font-bold text-3xl sm:text-4xl text-ink mb-10">
            Domande frequenti
          </motion.h2>
          <div className="space-y-4">
            {cfg.faqs.map((f, i) => (
              <motion.details key={f.q} {...fadeUp(i * 0.05)} className="group rounded-2xl border border-ink/10 bg-paper px-6 py-5" data-testid={`landing-faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-ink text-base list-none flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-azure text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="text-sm text-ink/60 leading-relaxed mt-3">{f.a}</p>
              </motion.details>
            ))}
          </div>
          {cfg.approfondimento && (
            <motion.div {...fadeUp(0.1)} className="mt-8">
              <Link
                to={cfg.approfondimento.landing ? `/${cfg.approfondimento.slug}` : `/blog/${cfg.approfondimento.slug}`}
                data-testid="landing-approfondimento-link"
                className="flex items-center justify-between gap-4 rounded-2xl border border-azure/30 bg-azure/5 px-6 py-5 text-brandblue font-semibold text-sm hover:bg-azure/10 transition-colors"
              >
                {cfg.approfondimento.label}
                <ArrowDown size={16} className="-rotate-90 shrink-0" />
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* Form */}
      <section id="richiedi" className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
        <motion.div {...fadeUp()} className="bg-ink text-white rounded-3xl p-8 sm:p-12">
          {sent ? (
            <div className="text-center py-8" data-testid="landing-success">
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-azure/15 text-azure mb-5">
                <Check size={30} />
              </span>
              <h2 className="font-serif font-bold text-3xl mb-3">Richiesta ricevuta!</h2>
              <p className="text-white/65 max-w-md mx-auto">
                Un consulente della tua zona ti ricontatterà entro 24 ore lavorative.
                Se preferisci, chiamaci subito al <a href="tel:+390550317244" className="text-azure font-semibold">055 031 7244</a>.
              </p>
            </div>
          ) : (
            <>
              <h2 className="font-serif font-bold text-3xl mb-2">{cfg.formTitle}</h2>
              <p className="text-white/60 text-sm mb-8">{cfg.formSub}</p>
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="landing-form">
                <input required minLength={2} value={form.nome} onChange={set("nome")} placeholder="Nome e cognome *" data-testid="landing-nome-input" className={`${inputCls} !bg-white/5 !border-white/15 !text-white placeholder:!text-white/35`} />
                <input required minLength={6} value={form.telefono} onChange={set("telefono")} placeholder="Telefono *" type="tel" data-testid="landing-telefono-input" className={`${inputCls} !bg-white/5 !border-white/15 !text-white placeholder:!text-white/35`} />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" data-testid="landing-email-input" className={`${inputCls} !bg-white/5 !border-white/15 !text-white placeholder:!text-white/35`} />
                <input required minLength={2} value={form.citta} onChange={set("citta")} placeholder="Città dell'immobile *" data-testid="landing-citta-input" className={`${inputCls} !bg-white/5 !border-white/15 !text-white placeholder:!text-white/35`} />
                <select value={form.tipologia} onChange={set("tipologia")} data-testid="landing-tipologia-select" className={`${inputCls} !bg-white/5 !border-white/15 !text-white sm:col-span-2`}>
                  {["Appartamento", "Villa / Villetta", "Altro"].map((t) => (
                    <option key={t} value={t} className="text-ink">{t}</option>
                  ))}
                </select>
                <input value={form.indirizzo} onChange={set("indirizzo")} placeholder="Indirizzo e mq dell'immobile (per il calcolo del canone)" data-testid="landing-indirizzo-input" className={`${inputCls} !bg-white/5 !border-white/15 !text-white placeholder:!text-white/35 sm:col-span-2`} />
                <textarea value={form.note} onChange={set("note")} placeholder="Note (metratura, stato, quando vorresti affittare...)" rows={3} data-testid="landing-note-input" className={`${inputCls} !bg-white/5 !border-white/15 !text-white placeholder:!text-white/35 sm:col-span-2 resize-none`} />
                <label className="sm:col-span-2 flex items-start gap-3 text-xs text-white/55 leading-relaxed cursor-pointer">
                  <input type="checkbox" checked={form.privacy} onChange={set("privacy")} data-testid="landing-privacy-check" className="mt-0.5 accent-azure" />
                  <span>
                    Ho letto e accetto l'<Link to="/privacy" className="underline text-white/75">informativa privacy</Link> *
                  </span>
                </label>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="landing-submit-btn"
                    className="w-full inline-flex justify-center items-center gap-2 bg-gold hover:bg-gold/90 disabled:opacity-60 text-ink font-semibold rounded-full px-6 py-4 text-sm transition-colors"
                  >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    Invia la richiesta gratuita
                  </button>
                </div>
              </form>
            </>
          )}
        </motion.div>
      </section>

      {/* Footer minimo */}
      <footer className="border-t border-ink/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-wrap items-center justify-between gap-4 text-xs text-ink/45">
          <img src="/assets/logo-nuovo.png" alt="Semplificaffitto.it" className="h-5 w-auto" />
          <span>Edilstudio S.r.l. · P.IVA 02447670973 · Scandicci (FI)</span>
          <div className="flex gap-5">
            <Link to="/" className="hover:text-ink transition-colors">Sito principale</Link>
            <Link to="/privacy" className="hover:text-ink transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
